import { useRef, useState } from 'react';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useToast } from './Toast';
import { Confirm } from './Confirm';

const TEXTO = {
  pausar: '¿Pausar {mla} en Mercado Libre? Deja de venderse hasta que alguien la active. La sincronización no la va a activar sola.',
  activar: '¿Activar {mla} en Mercado Libre? Vuelve a venderse con las unidades que tiene en Mercado Libre.',
};

/** «Pausar» (activa) o «Activar» (pausada) a mano, sólo admin. Otros estados: nada. */
export function BotonEstadoPublicacion({ mlaId, status, onCambio }: { mlaId: string; status: string | null; onCambio: () => void }) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [abierto, setAbierto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const enCurso = useRef(false); // el estado no alcanza: dos clics en el mismo tick leen el mismo valor
  const accion = status === 'active' ? 'pausar' : status === 'paused' ? 'activar' : null;
  if (user?.role !== 'admin' || !accion) return null;

  const confirmar = async () => {
    if (enCurso.current) return;
    enCurso.current = true;
    setEnviando(true);
    try {
      const r = await api.post<{ mensaje: string }>(`/api/v3/ml/publicaciones/${mlaId}/estado`, { accion });
      addToast(r.mensaje, 'success');
      setAbierto(false);
      onCambio();
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
      setAbierto(false);
    } finally {
      enCurso.current = false;
      setEnviando(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setAbierto(true)} className="text-xs text-brand-600 hover:text-brand-800 underline">
        {accion === 'pausar' ? 'Pausar' : 'Activar'}
      </button>
      <Confirm
        open={abierto}
        title={accion === 'pausar' ? 'Pausar publicación' : 'Activar publicación'}
        message={TEXTO[accion].replace('{mla}', mlaId)}
        confirmLabel="Confirmar"
        disabled={enviando}
        danger={accion === 'pausar'}
        onCancel={() => setAbierto(false)}
        onConfirm={() => void confirmar()}
      />
    </>
  );
}

interface FilaReenvio {
  mlaId: string;
  variantId: string | null;
  stockMl: number | null;
  stockCalculado: number | null;
  ultimaEscrituraErp: string | null;
  retenida: boolean;
  skusSinHabilitar: string[];
}

const rtf = new Intl.RelativeTimeFormat('es-AR', { numeric: 'auto' });
/** "hace 5 minutos", "ayer"… */
function hace(iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  const [v, u]: [number, Intl.RelativeTimeFormatUnit] = s < 60 ? [s, 'second'] : s < 3600 ? [s / 60, 'minute'] : s < 86400 ? [s / 3600, 'hour'] : [s / 86400, 'day'];
  return rtf.format(-Math.round(v), u);
}

const AVISO_RETENIDA = (n: number | null) =>
  'Mercado Libre muestra menos de lo que escribió el ERP y no hay una venta que lo explique: puede ser un cambio a mano o una venta que todavía no llegó. '
  + `Si reenviás, Mercado Libre vuelve a mostrar ${n ?? '—'}.`;

/** «Reenviar stock ahora» (sólo admin): muestra qué se escribiría en cada publicación y, al confirmar, lo reenvía. */
export function BotonReenviarStock({ sku, onHecho, todoElSku = false }: { sku: string; onHecho?: () => void; todoElSku?: boolean }) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [filas, setFilas] = useState<FilaReenvio[] | null>(null);
  const [enviando, setEnviando] = useState(false);
  const enCurso = useRef(false);
  if (user?.role !== 'admin') return null;

  const abrir = async () => {
    try {
      const p = await api.get<{ publicaciones: FilaReenvio[] }>(`/api/v3/ml/sync-sku/preview?sku=${encodeURIComponent(sku)}`);
      const escribibles = p.publicaciones.filter((f) => f.skusSinHabilitar.length === 0);
      if (escribibles.length === 0) {
        addToast('No hay publicaciones de este SKU que se puedan escribir en Mercado Libre.', 'error');
        return;
      }
      setFilas(escribibles);
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
    }
  };

  const confirmar = async () => {
    if (enCurso.current || !filas) return;
    enCurso.current = true;
    setEnviando(true);
    try {
      const r = await api.post<{ enviados: number; fallidos: number; retenidas?: string[]; resultados?: Array<{ ok?: boolean; error?: string }> }>(
        '/api/v3/ml/sync-sku/reenviar', { sku, forzarRetenidas: filas.some((f) => f.retenida) },
      );
      const errores = (r.resultados ?? []).filter((x) => x.error).map((x) => x.error);
      if (r.fallidos > 0 || errores.length > 0) addToast(`Con errores: ${errores.join('; ') || `${r.fallidos} fallaron`}`, 'error');
      else if (r.enviados === 0 && (r.retenidas?.length ?? 0) > 0) {
        addToast('No se reenvió: Mercado Libre muestra menos de lo que escribió el ERP. Volvé a tocar «Reenviar stock ahora» para ver el detalle.', 'error');
      } else addToast(`Stock reenviado: ${r.enviados} ${r.enviados === 1 ? 'publicación' : 'publicaciones'}`, 'success');
      setFilas(null);
      onHecho?.();
    } catch (e) {
      addToast(extractErrorMessage(e), 'error');
      setFilas(null);
    } finally {
      enCurso.current = false;
      setEnviando(false);
    }
  };

  const mensaje = filas
    ? [
      todoElSku ? `Se reenvía el stock de todas las publicaciones de ${sku}.` : null,
      ...filas.flatMap((f) => [
        `${f.mlaId}${f.variantId ? ` / variante ${f.variantId}` : ''}: Mercado Libre muestra ${f.stockMl ?? '—'} · el ERP publicaría ${f.stockCalculado ?? '—'} · ${f.ultimaEscrituraErp ? `el ERP la escribió ${hace(f.ultimaEscrituraErp)}` : 'el ERP nunca la escribió'}`,
        f.retenida ? AVISO_RETENIDA(f.stockCalculado) : null,
      ]),
    ].filter(Boolean).join('\n')
    : '';

  return (
    <>
      <button type="button" onClick={() => void abrir()} className="text-xs text-brand-600 hover:text-brand-800 underline">
        Reenviar stock ahora
      </button>
      <Confirm
        open={filas !== null}
        title={`Reenviar el stock de ${sku} a Mercado Libre`}
        message={mensaje}
        confirmLabel="Reenviar stock ahora"
        disabled={enviando}
        onCancel={() => setFilas(null)}
        onConfirm={() => void confirmar()}
      />
    </>
  );
}
